import { BORNES_BULLE } from '@nextilt/commun';
import { estComplete, type Bornes } from './saisieTapee';

/**
 * Les deux cases de la durée dans la fiche, heures et minutes (chantier duree-a-la-minute,
 * ticket 03, décision d'Igor du 2026-10-11 : « taper 1 h 25 sans avoir à compter le nombre de
 * minutes »). La tâche garde le total en minutes (`effortMin`) ; les cases ne sont qu'une façon
 * de le taper. Sans rendu : la case elle-même est `CasesDeLaDuree.tsx`.
 *
 * Les règles de « Quand » (`saisieTapee.ts`) : une case vide compte 0 ; 0 et 0 enlève la durée ;
 * une case hors de ses bornes, ou un total au-delà de celui du serveur (24 h), rend la valeur
 * d'avant, comme une valeur impossible.
 */

const MINUTES_PAR_HEURE = 60;

/** Les heures : de 0 à 24, la borne du serveur (`BORNES_BULLE.effortMinMax`, 1 440 minutes). */
export const BORNES_DES_HEURES: Bornes = { min: 0, max: BORNES_BULLE.effortMinMax / MINUTES_PAR_HEURE, pas: 1 };
/** Les minutes : de 0 à 59 ; au-delà, c'est une heure de plus, à taper dans l'autre case. */
export const BORNES_DES_MINUTES: Bornes = { min: 0, max: MINUTES_PAR_HEURE - 1, pas: 1 };

/** Ce que disent les deux cases. */
export interface DeuxCases {
  heures: string;
  minutes: string;
}

/** La durée posée, relue dans les deux cases : 85 donne 1 et 25 ; un zéro laisse sa case vide. */
export function casesDeLaDuree(effortMin: number | null): DeuxCases {
  if (effortMin == null) return { heures: '', minutes: '' };
  const heures = Math.floor(effortMin / MINUTES_PAR_HEURE);
  const minutes = effortMin % MINUTES_PAR_HEURE;
  return { heures: heures ? String(heures) : '', minutes: minutes ? String(minutes) : '' };
}

export type IssueDesCases = { sorte: 'ecrire'; effortMin: number | null } | { sorte: 'rien' } | { sorte: 'revenir' };

/**
 * Ce qu'on fait en quittant les deux cases : écrire le total (`null` pour 0 et 0), ne rien faire
 * (la même durée qu'avant), ou rendre la valeur d'avant (une case impossible, ou à moitié tapée
 * selon le navigateur, `incomplet`).
 */
export function issueDesCases(heures: string, minutes: string, avant: number | null, incomplet = false): IssueDesCases {
  if (incomplet) return { sorte: 'revenir' };
  if (!estComplete('nombre', heures, BORNES_DES_HEURES) || !estComplete('nombre', minutes, BORNES_DES_MINUTES)) {
    return { sorte: 'revenir' };
  }
  const total = Number(heures || 0) * MINUTES_PAR_HEURE + Number(minutes || 0);
  if (total > BORNES_BULLE.effortMinMax) return { sorte: 'revenir' };
  const effortMin = total === 0 ? null : total;
  if (effortMin === avant) return { sorte: 'rien' };
  return { sorte: 'ecrire', effortMin };
}
